import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import SortableTable from "../../components/SortableTable";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import Modal from "../../components/Modal";
import Input from "../../components/Input";
import InputFile from "../../components/InputFile";
import InputSelect from "../../components/InputSelect";
import BoxTabs from "../../components/BoxTabs";
import { ModalDelete } from "../../components/ModalDelete";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { canEditBrands } from "../../services/auth";

// Endpoint de banners da marca já publicado: a aba busca os banners cadastrados.
const BANNERS_ENDPOINT_READY = true;

const statusOptions = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false }
];

const statusFilterOptions = [
    { label: 'Todos os status', value: null },
    { label: 'Ativo', value: 'active' },
    { label: 'Inativo', value: 'inactive' }
];

const formatPixels = value => Number(value).toLocaleString('pt-BR');

const toTypeOption = format => ({
    label: `${format.label} (${formatPixels(format.width)}px por ${formatPixels(format.height)}px)`,
    aba: format.label,
    value: format.type
});

const targetOptions = [
    { label: 'Não se aplica', value: null },
    { label: 'Nova aba', value: '_blank' },
    { label: 'Mesma aba', value: '_self' }
];

// `theme` é o tema selecionado na edição, mesmo antes do Salvar: os tipos de
// banner são os formatos dele (themes.banner_formats).
export const BannersTab = ({ brandId, theme }) => {
    const typeOptions = (theme?.banner_formats ?? []).map(toTypeOption);

    const [loading, setLoading] = useState(true);
    const [banners, setBanners] = useState([]);
    const [data, setData] = useState({ header: [], rows: {} });

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);

    const [id, setId] = useState('');
    const [name, setName] = useState('');
    const [status, setStatus] = useState('');
    const [position, setPosition] = useState('');
    const [type, setType] = useState('');
    const [link, setLink] = useState('');
    const [target, setTarget] = useState('');

    const [imageBase64, setImageBase64] = useState('');
    const [imageName, setImageName] = useState('');

    const [bannerToDelete, setBannerToDelete] = useState(null);

    useEffect(() => {
        // Só busca quando o endpoint de banners da marca existir (evita 401 -> /login).
        if (BANNERS_ENDPOINT_READY) {
            getBanners();
        } else {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        generateData();
    }, [banners, search, statusFilter, theme]);

    useEffect(() => {
        if (!showCreate) clean();
    }, [showCreate]);

    useEffect(() => {
        if (!showEdit) clean();
    }, [showEdit]);

    const getBanners = () => {
        setLoading(true);
        api.get(`/brand/${brandId}/banner`).then(res => {
            setBanners(res.data.resources ?? []);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    };

    const filteredBanners = () => {
        return banners.filter(banner => {
            if (search && !(banner.name ?? '').toLowerCase().includes(search.toLowerCase())) return false;
            if (statusFilter === 'active' && !banner.status) return false;
            if (statusFilter === 'inactive' && banner.status) return false;
            return true;
        });
    };

    const generateData = () => {
        const header = ['Imagem', 'Nome', 'Status', 'Link'];
        const rows = {};
        const list = filteredBanners();

        // Com filtro ativo a lista da tela não é a ordem real, então a linha vai
        // sem `id` — o SortableTable trata quem não tem id como linha fixa e o
        // arrasto não reordena um recorte.
        const sortable = !search && !statusFilter;

        typeOptions.map(typeOption => {
            rows[typeOption.value] = list
                .filter(banner => banner.type === typeOption.value)
                .sort((a, b) => a.position - b.position)
                .map(banner => ({
                    ...(sortable && { id: banner.id }),
                    data: [
                        <img src={banner.image_url?.small} alt={banner.name} />,
                        banner.name,
                        <span className={`brand-status-badge ${banner.status ? 'ok' : 'pending'}`}>{banner.status ? 'Ativo' : 'Inativo'}</span>,
                        banner.link
                    ],
                    action: () => editBanner(banner)
                }));
        });

        setData({ header, rows });
    };

    // Reordena por arrasta e solta. Atualiza otimista e reverte se o backend falhar.
    const reorderBanners = orderedIds => {
        const previous = banners;

        const positionById = {};
        orderedIds.forEach((bannerId, index) => { positionById[bannerId] = index + 1; });

        setBanners(banners.map(banner => (
            positionById[banner.id] != null
                ? { ...banner, position: positionById[banner.id] }
                : banner
        )));

        api.put(`/brand/${brandId}/banner/reorder`, { ids: orderedIds }).catch(error => {
            setBanners(previous);
            renderError(error);
        });
    };

    const storeBanner = () => {
        setLoading(true);

        // Banner novo entra no fim da lista do seu tipo; a ordem fina sai do
        // arrasta e solta.
        const typePositions = banners.filter(banner => banner.type === type).map(banner => banner.position);
        const nextPosition = typePositions.length ? Math.max(...typePositions) + 1 : 1;

        api.post(`/brand/${brandId}/banner`, {
            name, status, position: nextPosition, type, link, target, image: imageBase64
        }).then(() => {
            getBanners();
            setShowCreate(false);
            toast.success('Banner cadastrado com sucesso');
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    };

    const editBanner = banner => {
        setId(banner.id);
        setName(banner.name);
        setStatus(banner.status);
        setPosition(banner.position);
        setType(banner.type);
        setLink(banner.link);
        setTarget(banner.target);
        setImageBase64('');
        setImageName(banner.image_url?.small);
        setShowEdit(true);
    };

    const updateBanner = () => {
        setLoading(true);

        api.put(`/brand/${brandId}/banner/${id}`, {
            name, status, position, type, link, target, image: imageBase64
        }).then(() => {
            getBanners();
            setShowEdit(false);
            toast.success('Banner atualizado com sucesso');
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    };

    const clean = () => {
        setId('');
        setName('');
        setStatus('');
        setPosition('');
        setType('');
        setLink('');
        setTarget('');
        setImageBase64('');
        setImageName('');
    };

    const deleteBanner = () => {
        setLoading(true);

        api.delete(`/brand/${brandId}/banner/${bannerToDelete}`).then(() => {
            setBannerToDelete(null);
            setShowEdit(false);
            getBanners();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    };

    if (theme && typeOptions.length === 0) {
        return (
            <div className="brand-banners">
                <div className="brand-tab-head">
                    <div>
                        <h2 className="section-title">Banners da marca</h2>
                    </div>
                </div>
                <div className="brand-empty brand-empty-state">
                    <span className="brand-empty-icon">
                        <Icon name="image-off" size={28} strokeWidth={1.75} />
                    </span>
                    <strong>O tema {theme.name} não exibe banners</strong>
                    <p>Os banners já cadastrados ficam guardados e voltam a aparecer se a marca trocar para um tema com banners.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="brand-banners">
            {(showCreate || showEdit) &&
                <Modal
                    title={`${showCreate ? 'Cadastrar' : 'Editar'} banner`}
                    show={true}
                    close={() => showCreate ? setShowCreate(false) : setShowEdit(false)}
                    footer={
                        <>
                            {showEdit && canEditBrands() &&
                                <Button type={`secondary`} size={`small`} text={`Excluir`} svg={`delete-small`} action={() => setBannerToDelete(id)} loading={loading} />
                            }
                            {canEditBrands() &&
                                <Button type={`primary`} size={`small`} text={showCreate ? 'Cadastrar' : 'Salvar alterações'} svg={`save-white`} action={() => showCreate ? storeBanner() : updateBanner()} loading={loading} />
                            }
                        </>
                    }
                >
                    <div className="section">
                        <div className="row">
                            <div className="col-6">
                                <InputSelect label={`Status`} options={statusOptions} value={status} change={setStatus} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <Input type={`text`} label={`Nome`} value={name} change={setName} />
                            </div>
                            <div className="col-6">
                                <InputFile label={`Imagem`} base64={imageBase64} changeBase64={setImageBase64} name={imageName} changeName={setImageName} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <InputSelect label={`Tipo`} options={typeOptions} value={type} change={setType} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6">
                                <Input type={`text`} label={`Link`} value={link} change={setLink} />
                            </div>
                            <div className="col-6">
                                <InputSelect label={`Abertura em`} options={targetOptions} value={target} change={setTarget} />
                            </div>
                        </div>
                    </div>
                </Modal>
            }
            <ModalDelete
                show={bannerToDelete !== null}
                action={deleteBanner}
                close={() => setBannerToDelete(null)}
                loading={loading}
            />

            <div className="brand-tab-head">
                <div>
                    <h2 className="section-title">Banners da marca</h2>
                    <p className="section-hint">Gerencie os banners e os blocos visuais exibidos no site da marca.</p>
                </div>
                {canEditBrands() &&
                    <Button type={`primary`} size={`small`} text={`Adicionar banner`} svg="plus-white" action={() => { setType(typeOptions[activeTab]?.value ?? ''); setShowCreate(true); }} />
                }
            </div>

            <div className="brand-list-toolbar">
                <div className="brand-search">
                    <input
                        type="text"
                        placeholder="Buscar banner pelo nome"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <span className="brand-search-icon" aria-hidden="true">
                        <Icon name="search" size={16} />
                    </span>
                </div>
                <div className="brand-category-select">
                    <InputSelect value={statusFilter} change={setStatusFilter} options={statusFilterOptions} removeMargin={true} />
                </div>
            </div>

            <BoxTabs
                tab={activeTab}
                setTab={setActiveTab}
                titles={typeOptions.map(typeOption => typeOption.aba)}
                contents={typeOptions.map(typeOption => (
                    <SortableTable
                        header={data.header}
                        rows={data.rows[typeOption.value]}
                        loading={loading}
                        onReorder={reorderBanners}
                    />
                ))}
            />
        </div>
    );
};

export default BannersTab;
